// Portfolio Administration v1.0 PR 5 — cross-portfolio Local Enforcers
// registry list page at /portfolio-admin/local-enforcers. Replaces the
// PR 0 placeholder. Mirrors the VendorsListPage shape (same
// RegistryListLayout primitive); enforcer-specific bits are columns +
// filter chips + create modal. Behind PortfolioAdminV1; falls back to
// PR 0 placeholder when the flag is off.

import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureFlag } from "@/hooks/portfolioAdmin/useFeatureFlag";
import { useLocalEnforcersList } from "@/hooks/portfolioAdmin/useRegistries";
import { PlaceholderPage } from "@/pages/core/PlaceholderPage";
import { RegistryListLayout } from "@/components/portfolioAdmin/registry/RegistryListLayout";
import { LocalEnforcersListFilters } from "@/components/portfolioAdmin/localEnforcers/LocalEnforcersListFilters";
import { LocalEnforcersListTable } from "@/components/portfolioAdmin/localEnforcers/LocalEnforcersListTable";
import { CreateLocalEnforcerModal } from "@/components/portfolioAdmin/localEnforcers/CreateLocalEnforcerModal";

function arrayParam(params: URLSearchParams, key: string): string[] {
  const raw = params.getAll(key);
  if (raw.length === 0) return [];
  return raw.flatMap((v) => v.split(",")).filter(Boolean);
}

export default function LocalEnforcersListPage() {
  const flagOn = useFeatureFlag("PortfolioAdminV1");
  if (!flagOn) {
    return (
      <PlaceholderPage
        phase="Phase 3"
        title="Local Enforcers"
        description="Workout counterparty registry — law firms, collection agencies, receivers, process servers."
      />
    );
  }
  return <LocalEnforcersListPageInner />;
}

function LocalEnforcersListPageInner() {
  const auth = useAuth();
  const [params, setParams] = useSearchParams();
  const types = arrayParam(params, "type");
  const statuses = arrayParam(params, "status");
  const jurisdictions = arrayParam(params, "jurisdiction");
  const search = params.get("search") ?? "";
  const [modalOpen, setModalOpen] = useState(false);

  const canAdd =
    auth.hasRoleCode("E1") ||
    auth.hasRoleCode("ADMIN") ||
    auth.hasPermission("Enforcer.Manage");

  const writeParams = useCallback(
    (mutate: (p: URLSearchParams) => void) => {
      const next = new URLSearchParams(params);
      mutate(next);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const setMulti = (key: string) => (values: string[]) =>
    writeParams((p) => {
      p.delete(key);
      values.forEach((v) => p.append(key, v));
    });

  const setSearch = (next: string) =>
    writeParams((p) => {
      if (next) p.set("search", next);
      else p.delete("search");
    });

  const clearFilters = () =>
    writeParams((p) => {
      p.delete("type");
      p.delete("status");
      p.delete("jurisdiction");
      p.delete("search");
    });

  const listQuery = useLocalEnforcersList({
    types: types.length ? types : undefined,
    statuses: statuses.length ? statuses : undefined,
    jurisdictions: jurisdictions.length ? jurisdictions : undefined,
    search: search || undefined,
  });

  const rows = useMemo(() => listQuery.data ?? [], [listQuery.data]);

  const countSummary = listQuery.isLoading
    ? "Loading local enforcers…"
    : `Showing ${rows.length} ${rows.length === 1 ? "local enforcer" : "local enforcers"}`;

  return (
    <>
      <RegistryListLayout
        title="Local Enforcers"
        testIdPrefix="enforcers"
        actions={
          canAdd ? (
            <Button
              type="button"
              onClick={() => setModalOpen(true)}
              data-testid="enforcers-add-button"
            >
              <Plus size={16} aria-hidden="true" /> Add local enforcer
            </Button>
          ) : undefined
        }
        filters={
          <LocalEnforcersListFilters
            types={types}
            statuses={statuses}
            jurisdictions={jurisdictions}
            search={search}
            onTypesChange={setMulti("type")}
            onStatusesChange={setMulti("status")}
            onJurisdictionsChange={setMulti("jurisdiction")}
            onSearchChange={setSearch}
            onClear={clearFilters}
          />
        }
        countSummary={countSummary}
      >
        <LocalEnforcersListTable enforcers={rows} />
      </RegistryListLayout>
      <CreateLocalEnforcerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
}
